// All RunLoop site copy + structured content lives here (copy preserved as-is).

export const NAV_LINKS = [
  { label: "Home", to: "#top" },
  { label: "About", to: "#about" },
  { label: "Process", to: "#process" },
  { label: "Work", to: "#work" },
  { label: "Loop Lab", to: "/loop-lab", pill: true },
];

export const HERO = {
  eyebrow: "Automations and product systems",
  missionParts: [
    { t: "We are on a mission to put 10 million hours back into business.", em: false },
  ],
  subhead: "Most businesses run on more manual work than they should.",
  sub: "We help you run on less.",
  badge: "7 loops running right now",
};

export const TICKER_1 = {
  prefix: "You didn't hire a great team to",
  words: ["chase approvals", "update trackers", "forward emails", "copy data between tools"],
};

export const ABOUT = {
  eyebrow: "About",
  title: ["RunLoop is an", "efficiency partner."],
  lead: "Most businesses run on more manual work than they should. We help you run on less.",
  body:
    "Less manual work. Less complexity. Less time. Through intelligent, self-improving loops that handle the operational work, so your team can focus on the work that actually needs them.",
};

export const MISSION = {
  eyebrow: "Mission",
  headParts: [
    { t: "We believe the best teams shouldn't be ", em: false },
    { t: "buried in busywork", em: true },
    { t: " — they should be building.", em: false },
  ],
};

export const ENGINE = {
  eyebrow: "Our process",
  titleParts: [
    { t: "The ", em: false },
    { t: "loop", em: true },
    { t: " engine.", em: false },
  ],
  steps: [
    { n: "01", key: "Signal", desc: "Map your business operation.", detail: "We sit inside your workflow and map every signal — where work starts, who touches it, where it stalls." },
    { n: "02", key: "Pattern", desc: "Find the friction.", detail: "We surface the repeat patterns and the friction that quietly eats your team's hours." },
    { n: "03", key: "System", desc: "Build the solution.", detail: "We design and build the system — automations and product that fit the way you already work." },
    { n: "04", key: "Loop", desc: "Set it in motion.", detail: "We put it live. The loop runs the operational work on its own, in the background." },
    { n: "05", key: "Lift", desc: "Improve continuously.", detail: "The loop learns. It gets sharper, faster and cheaper every cycle — a system that lifts itself." },
  ],
};

export const WHO = {
  eyebrow: "Who it's for",
  titleParts: [
    { t: "We work with businesses that run on ", em: false },
    { t: "complexity.", em: true },
  ],
  industries: [
    { n: "01", name: "Construction & Real Estate", line: "Tenders, approvals and site data that never sit in one place." },
    { n: "02", name: "F&B", line: "Orders, inventory and outlets moving faster than a spreadsheet can." },
    { n: "03", name: "Fintech", line: "Reconciliation, compliance and reporting that can't afford a miss." },
    { n: "04", name: "Healthcare", line: "Records, intake and scheduling where every handoff matters." },
    { n: "05", name: "Logistics", line: "Shipments, exceptions and tracking across too many tools." },
  ],
};

export const CASES = [
  {
    slug: "capacite-infraprojects",
    caseNo: "Case 01",
    client: "Capacité Infraprojects",
    sector: "Construction & Real Estate",
    teaserParts: [
      { t: "Capacité has a BD team. We gave them ", em: false },
      { t: "sixteen extra analysts.", em: true },
    ],
    metrics: [
      { value: "16×", label: "BD coverage" },
      { value: "84", label: "signals a night" },
      { value: "0", label: "manual tracking" },
    ],
    result:
      "One always-on BD intelligence system — tenders, announcements and private leads, tracked continuously so nothing slips.",
    image: "/capacite_dashboard.png",
    detail: {
      problemEyebrow: "The problem",
      problem:
        "What we found, mapping it out, was where that process was really spending its time. Someone checks the government tender portals. Someone else scans the news for project announcements. Someone tracks LinkedIn for early signals; a factory groundbreaking, a developer's next move. At any given time, dozens of projects were moving through this pipeline at once, each one with an owner, tracked at the pace their day's other priorities left room for.",
      behindEyebrow: "The problem behind the problem",
      behindParts: [
        { t: "The system was only as strong as its most fragmented point, and with tracking spread across people, sources, and time, ", em: false },
        { t: "fragmentation was the default, not the exception.", em: true },
      ],
      image2: "/capacite_trail.png",
      endGoal: {
        eyebrow: "End goal",
        titleParts: [
          { t: "Closing the ", em: false },
          { t: "loop", em: true },
          { t: ".", em: false },
        ],
        paragraphs: [
          "Every project Capacité has ever spotted is now surfaced, sorted, and permanently on the radar.",
          "Capacité's BD team still does what they've always done well — running a serious business development process across many databases. What's changed? It all moves as one system now. Filtered intelligently, linked across time, delivered automatically, before the day starts.",
          "The same process, working as one system instead of many disconnected efforts.",
        ],
      },
      system: {
        eyebrow: "The system",
        intro: "We built Capacité a system that connects what their separated sources couldn't connect on their own.",
        features: [
          { n: "01", name: "Daily Bulletin", desc: "Every tender, announcement, and private lead from the last 24 hours, sorted and delivered to WhatsApp and email at 9:30 am, every morning." },
          { n: "02", name: "Interactive Dashboard", desc: "The full, filterable corpus, always current, searchable by sector, geography, or tender type." },
          { n: "03", name: "Continuity Trail", desc: "When a project resurfaces months later as a tender, it's linked back automatically." },
          { n: "04", name: "Smart Filtering", desc: "Every signal is checked against geography rules and value thresholds, with the ability to filter further for exactly what the team is looking for. Only what's relevant gets through." },
          { n: "05", name: "Configuration & Source Management", desc: "Sources, keywords, and thresholds can be added or changed any time, without touching code, or waiting on a rebuild. As priorities shift, the system shifts with them." },
          { n: "06", name: "Source Health Monitoring", desc: "Every source tracked live. An outage or broken feed gets flagged immediately instead of going unnoticed." },
          { n: "07", name: "Self-Learning Source Database", desc: "The system gets sharper over time, flagging patterns across news, government sites, and filings that a one-off manual check would never catch twice." },
          { n: "08", name: "Weekly Consolidated Report", desc: "Six days of intelligence rolled into one structured report every Monday, ready for the weekly meeting's brief." },
        ],
      },
    },
  },
];

export const TICKER_2 = {
  pre: "Building a",
  words: ["fundable", "scalable", "profitable"],
  post: "business is hard.",
  punch: "Running it shouldn't be.",
};

export const CTA = {
  eyebrow: "Let's talk",
  title: "Get Looped In",
  body:
    "Not a proposal. Not a commitment. Just a look at how your business operates, and an honest conversation about where it can run on less.",
  reassurance: ["Reply within one business day", "No obligation", "We map before we build"],
};

export const MARQUEE_WORDS = ["Close the loop", "Run on less", "Automate the busywork", "Efficiency, engineered"];
